import React, { useState } from "react";
import { Checkbox, DatePicker, Select, Button, message, Modal, Popconfirm, } from "antd";
import { DeleteOutlined, CloseOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../utils/axiosInstance";
import { capitalizeAndRemoveUnderscores } from "../../utils/utils";
import { getBoxColor, getCircleColor, getStatusBadgeColor } from "../../utils/Constant";
import ApprovalDetailsModal from "../modal/ApprovalDetailsModal";

const ApprovalTimeline = ({ user, company_id, fetchData, loading, setLoading, selectedUsers, setSelectedUsers, statusUsers, setStatusUsers, editingUserId, setEditingUserId, approvalMode, isRestrictedRole, canEdit }) => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const isDraftOrPending = user.status === "draft" || user.status === "pending";
    const isSentForApproval = (user.status || "").toLowerCase() === "sent for approval" || (user.status || "").toLowerCase() === "sent_for_approval";
    const isApprovedOrRejected = user.status.toLowerCase() === "approved" || user.status === "Rejected" || user.status === "Approval not required" || user.status === "Sent for approval";
    const currentlyEditing = editingUserId === user.id;

    const agreementStatus = [
        { value: "UNDER_NEGOTIATION", label: "Under Negotiation" },
        { value: "UNDER_EXECUTION", label: "Under Execution" },
        { value: "EXECUTED", label: "Executed" },
    ];

    const handleDeleteStatus = async (user_id, approval_token) => {
        if (isRestrictedRole) return;

        try {
            setLoading(true);
            await axiosInstance.delete(`contract/approval-status/delete/`, {
                data: { user_id, approval_token, company_id },
            });

            message.success("Approval status deleted successfully");

            // ✅ Await ensures the latest data loads before proceeding
            await fetchData();

            // ✅ Clear selection if deleted user was selected
            setSelectedUsers((prev) => prev.filter((id) => id !== user_id));

        } catch (error) {
            console.error(error);
            message.error("Failed to delete approval status");
        } finally {
            // ✅ Always stop loading, even if fetchData fails
            setLoading(false);
        }
    };

    const handleEditToggle = (userId) => {
        if (editingUserId === userId) {
            setEditingUserId(null);
            return;
        }
        setEditingUserId(userId);
        const key = user.approval_id ?? user.id;
        setSelectedUsers((prev) =>
            prev.includes(key) ? prev : [...prev, key]
        );
    };

    const saveApprovalEdit = async () => {
        if (isRestrictedRole) return;
        const specificUserApprovalId = user.approval_id;
        const foundUser = statusUsers.find(
            (u) => u.approval_id === specificUserApprovalId
        );
        if (!foundUser) return message.error("User not found.");

        const modeToUse =
            foundUser.status === "draft" || foundUser.status === "pending"
                ? "draft"
                : approvalMode || "serial";
        const payload = {
            approval_id: foundUser.approval_id,
            user_id: foundUser.id,
            company_id,
            approval_mode: modeToUse,
            scheduled_date: foundUser.scheduled_date
                ? dayjs(foundUser.scheduled_date).format("YYYY-MM-DDTHH:mm:ss")
                : null,
            remarks: foundUser.remarks || "",
            agreement_status:
                foundUser.scheduled_date
                    ? null
                    : foundUser.current_status || foundUser.agreement_status || null,
        };

        try {
            setLoading(true);
            console.log("📤 Edit Save Payload:", payload);
            await axiosInstance.post("/contract/resend-approval-email/", payload);
            message.success("Approval saved and resent (edit).");
            await fetchData();
            setEditingUserId(null);
        } catch (err) {
            console.error(err);
            message.error("Failed to save approval edits.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div key={user.id} className=" relative">
            <ApprovalDetailsModal
                visible={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                user={user}
            />
            <svg
                width="16" height="16" viewBox="0 0 10 10" className="absolute -left-[26px] top-1/2 -translate-y-1/2"
            >
                <circle
                    cx="5" cy="5" r="4"
                    stroke={getCircleColor(user.status.toLowerCase()).stroke}
                    fill={getCircleColor(user.status.toLowerCase).fill}
                    strokeWidth="1"
                />
            </svg>
            <div
                className={`p-2 rounded-lg shadow-md border flex justify-between items-start gap-4 ${getBoxColor(
                    user.status.toLowerCase()
                )}`}
            >
                <div className="flex flex-col ] w-full">
                    <div className="flex items-center gap-3">
                        {canEdit && (
                            <Checkbox
                                checked={selectedUsers.includes(user.approval_id ?? user.id)}
                                onChange={() =>
                                    setSelectedUsers((prev) =>
                                        prev.includes(user.approval_id ?? user.id)
                                            ? prev.filter((k) => k !== (user.approval_id ?? user.id))
                                            : [...prev, user.approval_id ?? user.id]
                                    )
                                }
                                disabled={isApprovedOrRejected}
                            />
                        )}
                        <div className="flex flex-col">
                            <span className="font-semibold flex justify-start gap-2 items-start  w-full text-[14px] dark:text-black">
                                {user.first_name} ({user.role})
                                <InfoCircleOutlined className=" cursor-pointer inline-block justify-tart items-center "
                                    onClick={() => setDetailsOpen(true)} style={{ fontSize: "16px", color: "#4E36A3" }} />
                            </span>
                            <span className="text-[12px] text-gray-500 dark:text-gray-700">{user.email}</span>

                            {user.remarks && !currentlyEditing ? (
                                <div className="flex flex-col">
                                    {user.remarks_grouped?.reject?.length > 0 && (
                                        <div className="text-[12px] text-red-800">
                                            <strong>Rejected Remark:</strong> {user.remarks_grouped.reject.join(", ")}
                                        </div>
                                    )}
                                    {user.remarks_grouped?.not_required?.length > 0 && (
                                        <div className="text-[12px] text-gray-800">
                                            <strong>Cancelled Remark:</strong> {user.remarks_grouped.not_required.join(", ")}
                                        </div>
                                    )}
                                    {/* {user.remarks_grouped?.send?.length > 0 && ( */}
                                        <div className="text-[12px] text-green-800">
                                            <strong>Send Remark:</strong> {user.remarks_grouped.send[user.remarks_grouped.send.length - 1] || user.remarks}
                                        </div>
                                    {/* )} */}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-4 pl-7 items-center dark:text-black">
                        {user.agreement_status && (
                            <div className="text-[12px]">
                                Status: <strong>{capitalizeAndRemoveUnderscores(user.agreement_status)}</strong>
                            </div>
                        )}
                        {user.scheduled_date && (
                            <div className="text-[12px]">
                                Scheduled Date:{" "}
                                <strong>{dayjs(user.scheduled_date).format("YYYY-MM-DD HH:mm")}</strong>
                            </div>
                        )}
                    </div>
                </div>
                {isDraftOrPending && (
                    <div className="flex flex-col gap-2 w-full">
                        {currentlyEditing &&
                            <div className="flex flex-col gap-2 w-full">
                                <div className="flex flex-wrap gap-2 items-center w-full bg-yellow-100 border-yellow-200 border justify-center p-1 rounded">
                                    <textarea
                                        placeholder="Add remarks..."
                                        className="border rounded p-2 w-full text-sm"
                                        value={user.remarks || ""}
                                        onChange={(e) =>
                                            setStatusUsers((prev) =>
                                                prev.map((u) =>
                                                    u.id === user.id ? { ...u, remarks: e.target.value } : u
                                                )
                                            )
                                        }
                                    />
                                    <div className="flex gap-1 w-full">

                                        <Select
                                            style={{ minWidth: 160 }}
                                            value={user.current_status || null}
                                            placeholder="Select Status"
                                            onChange={(val) =>
                                                setStatusUsers((prev) =>
                                                    prev.map((u) =>
                                                        u.id === user.id
                                                            ? { ...u, current_status: val, scheduled_date: null }
                                                            : u
                                                    )
                                                )
                                            }
                                            options={agreementStatus}
                                            disabled={!!user.scheduled_date || isRestrictedRole}
                                        />

                                        <DatePicker
                                            showTime
                                            value={user.scheduled_date ? dayjs(user.scheduled_date) : null}
                                            format="YYYY-MM-DD HH:mm"
                                            onChange={(val) =>
                                                setStatusUsers((prev) =>
                                                    prev.map((u) =>
                                                        u.id === user.id
                                                            ? { ...u, scheduled_date: val, current_status: null }
                                                            : u
                                                    )
                                                )
                                            }
                                            disabled={!!user.current_status || isRestrictedRole}
                                            disabledDate={(current) => current && current < dayjs().startOf("day")}
                                            disabledTime={(current) => {
                                                if (!current) return {};
                                                const now = dayjs();
                                                if (current.isSame(now, "day")) {
                                                    return {
                                                        disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
                                                        disabledMinutes: (hour) => (hour === now.hour() ? Array.from({ length: now.minute() }, (_, i) => i) : []),
                                                    };
                                                }
                                                return {};
                                            }}
                                        />
                                    </div>


                                    <button className="text-red-700 text-sm border px-2 py-[5px] border-red-700 rounded"
                                        onClick={() =>
                                            setStatusUsers((prev) =>
                                                prev.map((u) =>
                                                    u.id === user.id
                                                        ? { ...u, current_status: null, scheduled_date: null }
                                                        : u
                                                )
                                            )
                                        }> <CloseOutlined /></button>

                                    <Button
                                        type="primary"
                                        loading={loading}
                                        onClick={() => {
                                            const key = user.approval_id ?? user.id;
                                            if (!selectedUsers.includes(key)) {
                                                setSelectedUsers((prev) => [...prev, key]);
                                            }
                                            saveApprovalEdit(user.approval_id);
                                        }}
                                    >
                                        Save
                                    </Button>

                                    <button className="text-red-600 text-sm border px-2 py-[5px] border-red-600 rounded"
                                        onClick={() => {
                                            fetchData();
                                            setEditingUserId(null);
                                        }}
                                    >
                                        Cancel
                                    </button >
                                </div>
                            </div>
                        }
                    </div>
                )}
                <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center justify-end gap-2 w-[200px]">
                        <span
                            className={`text-[12px] max-w-[190px] font-semibold px-3 py-1 rounded-lg uppercase ${getStatusBadgeColor(
                                user.status.toLowerCase()
                            )}`}
                        >
                            {user.status}
                        </span>
                        {canEdit && user.approval_token && !["approved", "Approved", "Rejected", "Sent for approval", "Approval not required"].includes(user.status) && (
                            <Popconfirm
                                title="Are you sure to delete this status?"
                                onConfirm={() => handleDeleteStatus(user.id, user.approval_token)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button type="text" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                        )}
                    </div>
                    {isSentForApproval && canEdit && (
                        <div className="flex justify-end items-center w-full gap-2 ">
                            <div className="w-fit">
                                <button className="text-pink-600 text-[12px] bg-pink-200 border py-[5px] border-pink-400 rounded-xl px-3"
                                    onClick={() => {
                                        Modal.confirm({
                                            title: "Approval Not Required",
                                            content: (
                                                <div>
                                                    <p>Please provide a remark before confirming:</p>
                                                    <textarea
                                                        id={`approvalRemarkInput_${user.id}`}
                                                        rows={3}
                                                        placeholder="Enter remarks..."
                                                        className="w-full border rounded p-2 mt-2"
                                                    ></textarea>
                                                </div>
                                            ),
                                            okText: "Submit",
                                            cancelText: "Cancel",
                                            async onOk() {
                                                const remarks = document.getElementById(`approvalRemarkInput_${user.id}`)?.value?.trim();
                                                if (!remarks) {
                                                    message.error("Please enter a remark.");
                                                    throw new Error("No remark");
                                                }
                                                try {
                                                    const payload = {
                                                        approval_id: user.approval_id,
                                                        user_id: user.id,
                                                        company_id,
                                                        remarks,
                                                    };
                                                    await axiosInstance.post("/contract/approve-approvel_notrequired/", payload);
                                                    message.success("Marked as 'Approval Not Required' successfully.");
                                                    fetchData();
                                                } catch (error) {
                                                    console.error(error);
                                                    message.error("Failed to mark as 'Approval Not Required'.");
                                                }
                                            },
                                        });
                                    }}
                                >
                                    Approval Not Required
                                </button>

                            </div>
                        </div>
                    )}
                    {canEdit && !currentlyEditing && isDraftOrPending && (
                        <div className="w-full flex justify-end rounded">
                            <button className="text-yellow-600 text-[12px] bg-yellow-200 border py-[5px] border-yellow-400 rounded-xl px-3"
                                onClick={() => handleEditToggle(user.id)}
                            >
                                Edit
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApprovalTimeline;
